import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Box, Button, CircularProgress, Tooltip } from '@mui/material'
import { Refresh } from '@mui/icons-material'

import { connectionQueryKeys } from '@/api/services/connectionQueryKeys'

// Reads the connections again, along with everything shown under them, from the support API. It
// does not ask the bank provider to sync. That forced refresh (POST
// /users/{id}/bank-connections/refresh) is a separate action to add once the support API implements
// it; today it replies unavailable.
type Props = {
  // The customer this tab shows; only their reads are reloaded.
  userId: string
}

export const ReloadConnections: React.FC<Props> = ({ userId }) => {
  const queryClient = useQueryClient()
  const [isReloading, setIsReloading] = useState(false)

  const handleReload = async () => {
    setIsReloading(true)
    try {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: connectionQueryKeys.list(userId) }),
        queryClient.invalidateQueries({ queryKey: connectionQueryKeys.detail(userId) }),
        queryClient.invalidateQueries({ queryKey: connectionQueryKeys.accounts(userId) }),
        queryClient.invalidateQueries({ queryKey: connectionQueryKeys.syncHistory(userId) }),
        // Built from the same connections and accounts, and never re-read on its own.
        queryClient.invalidateQueries({ queryKey: connectionQueryKeys.transactionFilters(userId) }),
      ])
    } finally {
      setIsReloading(false)
    }
  }

  return (
    <Tooltip title="Reload all connections and their accounts with the latest data">
      {/* A disabled button fires no events, so the span carries the tooltip's hover. */}
      <Box component="span">
        <Button
          variant="outlined"
          size="small"
          onClick={handleReload}
          disabled={isReloading}
          startIcon={isReloading ? <CircularProgress size={14} color="inherit" /> : <Refresh />}
          sx={{ textTransform: 'none' }}
        >
          {isReloading ? 'Reloading…' : 'Reload'}
        </Button>
      </Box>
    </Tooltip>
  )
}
